// apiInterceptor.ts
import axios from 'axios';
import { BASE_URL } from './config';
import { tokenStorage } from './storage';

// Create Axios instance
export const appAxios = axios.create({
    baseURL: BASE_URL,
});

// Refresh tokens function
export const refreshTokens = async (): Promise<string | null> => {
    try {
        const refreshToken = tokenStorage.get('refreshToken');
        if (!refreshToken) {
            throw new Error('No refresh token found');
        }

        const response = await appAxios.post(`${BASE_URL}/oauth/refresh-token`, {
            refreshToken,
        });

        const newAccessToken = response.data.access_token;
        const newRefreshToken = response.data.refresh_token;

        // Update tokens in storage
        tokenStorage.set('accessToken', newAccessToken);
        tokenStorage.set('refreshToken', newRefreshToken);

        return newAccessToken;
    } catch (error) {
        console.error('REFRESH TOKEN ERROR:', error);
        tokenStorage.clearAll();
        window.location.href = '/auth/sign-in'; // Redirect to login
        return null;
    }
};

// Request interceptor
appAxios.interceptors.request.use((config) => {
    const accessToken = tokenStorage.get('accessToken');
    if (accessToken) {
        config.headers.Authorization = `Bearer ${accessToken}`;
    }
    return config;
});

// Response interceptor
appAxios.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest = error.config;

        // Handle 401 Unauthorized errors
        if (error.response && error.response.status === 401 && !originalRequest._retry) {
            originalRequest._retry = true; // Prevent infinite loops

            try {
                const newAccessToken = await refreshTokens();
                if (newAccessToken) {
                    originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
                    return appAxios(originalRequest); // Retry the original request
                }
            } catch (refreshError) {
                console.error('ERROR REFRESHING TOKEN:', refreshError);
            }
        }

        // Handle other errors
        if (error.response && error.response.status !== 401) {
            const errorMessage = error.response.data?.message || 'An error occurred';
            console.error(errorMessage);
        }

        return Promise.reject(error);
    }
);